/**
 * YUMIX — Settings panel : Sound Director toggle + volume + pack picker (Iter. 392).
 *
 * Lets the user enable/disable synthesized milestone sounds, tweak volume,
 * and pick between unlockable Sound Packs (Classic / Zen / Néon / 8-bit).
 *
 * Pack unlocks are validated SERVER-SIDE via /api/users/me/sound-pack — the
 * backend reads the user's longest_streak and returns the unlocked list.
 * The frontend just renders state + sends PATCH on click. Locked packs show
 * the unlock condition + a small lock icon and play a muted preview only.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import { Volume2, VolumeX, Trophy, Flame, Droplet, Target, Utensils, Clock, Music, Lock, Check, Sparkles, Leaf, Zap, Gamepad2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../context/AppContext';
import {
  isEnabled,
  setEnabled,
  getVolume,
  setVolume,
  play,
  setActivePack,
  getActivePack,
} from '../lib/soundDirector';

// Preview event grid (event id → icon + i18n labels).
const PREVIEW_EVENTS = [
  { id: 'badge_unlocked',     icon: Trophy,   label_fr: 'Badge débloqué',   label_en: 'Badge unlocked',     label_es: 'Insignia desbloqueada' },
  { id: 'streak_up',          icon: Flame,    label_fr: 'Streak +1',        label_en: 'Streak +1',          label_es: 'Racha +1' },
  { id: 'deficit_hit',        icon: Target,   label_fr: 'Déficit atteint',  label_en: 'Deficit hit',        label_es: 'Déficit logrado' },
  { id: 'meal_logged',        icon: Utensils, label_fr: 'Repas enregistré', label_en: 'Meal logged',        label_es: 'Comida registrada' },
  { id: 'water_glass',        icon: Droplet,  label_fr: 'Verre d\'eau',     label_en: 'Water glass',        label_es: 'Vaso de agua' },
  { id: 'fasting_milestone',  icon: Clock,    label_fr: 'Palier de jeûne',  label_en: 'Fasting milestone',  label_es: 'Etapa de ayuno' },
];

// Pack metadata (visual identity — backend owns labels & unlock rules).
const PACK_VISUALS = {
  classic: { icon: Sparkles,  accent: '#fb923c', tagline_fr: 'Signature YUMIX',         tagline_en: 'YUMIX signature',          tagline_es: 'Firma YUMIX' },
  zen:     { icon: Leaf,      accent: '#34d399', tagline_fr: 'Ondes douces, méditatif', tagline_en: 'Soft waves, meditative',   tagline_es: 'Ondas suaves, meditativo' },
  neon:    { icon: Zap,       accent: '#c084fc', tagline_fr: 'FM-bells brillantes',     tagline_en: 'Bright FM bells',          tagline_es: 'Campanas FM brillantes' },
  '8bit':  { icon: Gamepad2,  accent: '#f472b6', tagline_fr: 'Chiptune rétro NES',      tagline_en: 'Retro NES chiptune',       tagline_es: 'Chiptune retro NES' },
};

const labelFor = (item, language) => {
  if (language === 'en') return item.label_en;
  if (language === 'es') return item.label_es;
  return item.label_fr;
};

const SettingsSounds = ({ language = 'fr' }) => {
  const [on, setOn] = useState(isEnabled());
  const [vol, setVol] = useState(getVolume());
  // Pack state — `catalog` is the server-authoritative metadata.
  const [activePack, setActivePackLocal] = useState(getActivePack());
  const [unlocked, setUnlocked] = useState(['classic', 'zen']);
  const [catalog, setCatalog] = useState(null);
  const [longestStreak, setLongestStreak] = useState(0);
  const [loadingPack, setLoadingPack] = useState(false);

  // Load server-side pack state on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/users/me/sound-pack');
        if (!alive || !res?.data) return;
        const { active, unlocked: u, longest_streak, catalog: c } = res.data;
        if (active) {
          setActivePackLocal(active);
          setActivePack(active); // sync the audio engine
        }
        if (Array.isArray(u)) setUnlocked(u);
        if (c) setCatalog(c);
        if (typeof longest_streak === 'number') setLongestStreak(longest_streak);
      } catch (_) { /* offline — fall back to localStorage */ }
    })();
    return () => { alive = false; };
  }, []);

  const handleToggle = (next) => {
    setOn(next);
    setEnabled(next);
    if (next) {
      try { play('tap'); } catch (_) {}
    }
  };

  const handleVolume = (next) => {
    const v = Array.isArray(next) ? next[0] / 100 : next;
    setVol(v);
    setVolume(v);
  };

  const handlePreview = (eventId) => {
    if (!on) handleToggle(true);
    try { play(eventId); } catch (_) {}
  };

  const handlePackSelect = useCallback(async (packId) => {
    const isUnlocked = unlocked.includes(packId);
    if (!isUnlocked) {
      // Locked: tease with the active pack's badge sound + show toast.
      const required = catalog?.[packId]?.min_streak ?? '?';
      const remaining = Math.max(0, required - longestStreak);
      toast.error(
        language === 'fr'
          ? `🔒 Pack verrouillé · ${remaining} jour${remaining > 1 ? 's' : ''} de streak restant`
          : language === 'es'
          ? `🔒 Pack bloqueado · ${remaining} día${remaining > 1 ? 's' : ''} de racha restantes`
          : `🔒 Locked · ${remaining} streak day${remaining > 1 ? 's' : ''} to go`,
        { duration: 2500 }
      );
      if (on) { try { play('soft_error'); } catch (_) {} }
      return;
    }
    if (packId === activePack) {
      // Re-preview current pack with badge fanfare.
      if (on) { try { play('badge_unlocked'); } catch (_) {} }
      return;
    }
    setLoadingPack(true);
    try {
      const res = await api.patch('/users/me/sound-pack', { sound_pack: packId });
      const newActive = res?.data?.active || packId;
      setActivePack(newActive);
      setActivePackLocal(newActive);
      if (on) { try { play('badge_unlocked'); } catch (_) {} }
      toast.success(
        language === 'fr' ? '🎵 Pack sonore activé'
          : language === 'es' ? '🎵 Pack de sonido activado'
          : '🎵 Sound pack activated'
      );
    } catch (err) {
      toast.error(
        language === 'fr' ? 'Erreur lors du changement de pack'
          : language === 'es' ? 'Error al cambiar el pack'
          : 'Failed to switch pack'
      );
    } finally {
      setLoadingPack(false);
    }
  }, [activePack, unlocked, catalog, longestStreak, on, language]);

  // Compute the pack ID list to render. Prefer catalog order so backend
  // dictates the layout (future packs auto-appear).
  const packIds = catalog ? Object.keys(catalog) : ['classic', 'zen', 'neon', '8bit'];

  return (
    <Card
      className="bg-card border-border relative overflow-hidden"
      style={{ boxShadow: '0 0 0 1px rgba(251,146,60,0.20), 0 0 22px rgba(251,146,60,0.10)' }}
      data-testid="settings-sounds-panel"
    >
      <CardContent className="p-4 space-y-4">
        <div>
          <h3 className="font-heading text-base uppercase tracking-tight flex items-center gap-2 mb-1">
            <Music className="w-4 h-4 text-orange-400" style={{ filter: 'drop-shadow(0 0 4px rgba(251,146,60,0.7))' }} />
            {language === 'fr' ? 'Sons & feedback' : language === 'es' ? 'Sonidos y feedback' : 'Sounds & feedback'}
          </h3>
          <p className="text-xs text-muted-foreground">
            {language === 'fr'
              ? 'Sons synthétisés en temps réel — zéro fichier audio, fonctionne offline.'
              : language === 'es'
              ? 'Sonidos sintetizados en tiempo real — sin archivos, funciona sin conexión.'
              : 'Synthesized in real time — zero audio files, works offline.'}
          </p>
        </div>

        <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/40 border border-border/40">
          <Label htmlFor="sound-master" className="flex items-center gap-2 text-sm cursor-pointer">
            {on ? <Volume2 className="w-4 h-4 text-emerald-400" /> : <VolumeX className="w-4 h-4 text-muted-foreground" />}
            <span>{language === 'fr' ? 'Activer les sons de milestones' : language === 'es' ? 'Activar sonidos de hitos' : 'Enable milestone sounds'}</span>
          </Label>
          <Switch id="sound-master" checked={on} onCheckedChange={handleToggle} data-testid="settings-sounds-master-toggle" />
        </div>

        {on && (
          <>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground flex items-center justify-between">
                <span>{language === 'fr' ? 'Volume' : language === 'es' ? 'Volumen' : 'Volume'}</span>
                <span className="font-mono tabular-nums text-foreground/80">{Math.round(vol * 100)}%</span>
              </Label>
              <Slider
                min={0}
                max={100}
                step={5}
                value={[Math.round(vol * 100)]}
                onValueChange={handleVolume}
                data-testid="settings-sounds-volume"
              />
            </div>

            {/* ── PACK PICKER ────────────────────────────────────── */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {language === 'fr' ? 'Pack sonore' : language === 'es' ? 'Pack de sonido' : 'Sound pack'}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {language === 'fr' ? 'Streak max' : language === 'es' ? 'Racha máx' : 'Best streak'}
                  <span className="ml-1 font-mono tabular-nums text-orange-400">{longestStreak}🔥</span>
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2" data-testid="settings-sounds-pack-grid">
                {packIds.map(pid => {
                  const meta = catalog?.[pid] || { min_streak: 0 };
                  const vis = PACK_VISUALS[pid] || PACK_VISUALS.classic;
                  const Icon = vis.icon;
                  const isUnlocked = unlocked.includes(pid);
                  const isActive = pid === activePack;
                  const tagline = vis[`tagline_${language}`] || vis.tagline_fr;
                  const label = meta[`label_${language}`] || meta.label_fr || pid;
                  return (
                    <button
                      key={pid}
                      type="button"
                      disabled={loadingPack}
                      onClick={() => handlePackSelect(pid)}
                      data-testid={`settings-sounds-pack-${pid}`}
                      data-active={isActive ? 'true' : 'false'}
                      data-locked={isUnlocked ? 'false' : 'true'}
                      className={`relative p-3 rounded-lg border text-left transition-all overflow-hidden group ${
                        isActive
                          ? 'border-orange-500/60 bg-orange-500/10'
                          : isUnlocked
                          ? 'border-border/40 bg-secondary/40 hover:bg-secondary/70 hover:border-orange-500/30'
                          : 'border-border/30 bg-secondary/20 opacity-60 hover:opacity-80'
                      }`}
                      style={isActive ? { boxShadow: `0 0 14px ${vis.accent}55, inset 0 0 12px ${vis.accent}22` } : undefined}
                    >
                      {/* Active checkmark */}
                      {isActive && (
                        <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-orange-500/90 flex items-center justify-center" data-testid={`settings-sounds-pack-${pid}-active`}>
                          <Check className="w-3 h-3 text-white" strokeWidth={3} />
                        </div>
                      )}
                      {/* Lock badge */}
                      {!isUnlocked && (
                        <div className="absolute top-1.5 right-1.5 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/60 border border-white/10" data-testid={`settings-sounds-pack-${pid}-locked`}>
                          <Lock className="w-3 h-3 text-white/70" />
                        </div>
                      )}
                      <div className="flex items-start gap-2">
                        <div
                          className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                          style={{
                            background: `linear-gradient(135deg, ${vis.accent}33, ${vis.accent}11)`,
                            boxShadow: isUnlocked ? `0 0 10px ${vis.accent}55` : 'none',
                          }}
                        >
                          <Icon className="w-4 h-4" style={{ color: vis.accent, filter: isUnlocked ? `drop-shadow(0 0 4px ${vis.accent})` : 'none' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate" style={isActive ? { color: vis.accent } : undefined}>
                            {label}
                          </p>
                          <p className="text-[10px] text-muted-foreground line-clamp-2 leading-tight">
                            {tagline}
                          </p>
                          {!isUnlocked && (
                            <p className="text-[10px] mt-1 text-orange-400/80 font-mono">
                              {language === 'fr'
                                ? `🔓 Streak ${meta.min_streak}j`
                                : language === 'es'
                                ? `🔓 Racha ${meta.min_streak}d`
                                : `🔓 ${meta.min_streak}-day streak`}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── EVENT PREVIEW GRID ─────────────────────────────── */}
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
                {language === 'fr' ? 'Prévisualiser' : language === 'es' ? 'Previsualizar' : 'Preview'}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {PREVIEW_EVENTS.map(ev => {
                  const Icon = ev.icon;
                  return (
                    <button
                      key={ev.id}
                      type="button"
                      onClick={() => handlePreview(ev.id)}
                      data-testid={`settings-sounds-preview-${ev.id}`}
                      className="p-2.5 rounded-lg bg-secondary/40 hover:bg-orange-500/15 border border-border/40 hover:border-orange-500/40 transition-all flex items-center gap-2 text-left group"
                    >
                      <Icon className="w-4 h-4 text-orange-400 shrink-0 group-hover:scale-110 transition-transform" />
                      <span className="text-xs font-medium truncate">{labelFor(ev, language)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SettingsSounds;
